import { Component, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../../services/auth.service';
import { FNFResponse } from '../../../models/response';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { RestaurantService } from '../../../services/restaurant.service';
import { ImageCropingComponent } from '../../modals/image-croping/image-croping.component';
import {
  croppedImageFile,
  sourceImageError,
  uploadSizeError,
} from '../../../utils/image-export.util';
import { PhoneCountry, flagOf, formatNational, parsePhone } from '../../../utils/phone-number.util';

// business-user-service ignores a falsy img_path on update, so a single space
// is sent to clear the photo. Every reader trims img_path before using it.
const CLEARED_IMG_PATH = ' ';

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [ReactiveFormsModule, MatIcon, CommonModule],
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.css',
})
export class ProfileComponent {
  imagePreview: string | null = null;
  authService = inject(AuthService);
  restaurantService = inject(RestaurantService);
  ngxSpinnerService = inject(NgxSpinnerService);
  currentUser: any;
  profileForm: FormGroup;
  login_user_img_url: string = './../../../../assets/default/profile.png';
  imageSizeError: any;
  displayLoading: boolean = false;
  private selectedFile: File | null = null;
  phoneCountry: PhoneCountry = parsePhone('').country;
  phoneFlag = flagOf(this.phoneCountry);

  constructor(
    private dialog: MatDialog,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private router: Router,
  ) {
    this.profileForm = this.fb.group({
      first_name: ['', [Validators.required, Validators.minLength(3)]],
      last_name: ['', [Validators.required, Validators.minLength(3)]],
      email: [
        '',
        [Validators.required, Validators.pattern(/^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/)],
      ],
      display_name: ['', [Validators.required, Validators.minLength(3)]],
      phone_number: [''],
      img_path: [''],
    });
    this.profileForm.get('first_name')?.disable();
    this.profileForm.get('email')?.disable();
    this.profileForm.get('phone_number')?.disable();
  }

  ngOnInit() {
    this.getCurrentUser();
  }

  /** Stored as E.164; shown as flag + calling code, then the national number. */
  setPhoneNumber(stored: string) {
    const phone = parsePhone(stored);
    this.phoneCountry = phone.country;
    this.phoneFlag = flagOf(phone.country);
    return formatNational(phone.country, phone.national);
  }

  getCurrentUser() {
    this.displayLoading = true;
    this.authService.getCurrentUser().subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.displayLoading = false;
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.displayLoading = false;
          return;
        }
        this.currentUser = response.data;
        this.imagePreview = this.currentUser.img_path?.trim() || null;
        this.displayLoading = false;
        this.setProfileForm(this.currentUser);
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.displayLoading = false;
      },
    });
  }

  setProfileForm(data: any) {
    this.profileForm.patchValue({
      first_name: data.first_name + ' ' + data.last_name,
      last_name: data.last_name,
      email: data.email,
      display_name: data.display_name,
      phone_number: this.setPhoneNumber(data.phone_number || ''),
      img_path: data.img_path,
    });
    this.validateForm();
  }

  validateForm(): void {
    Object.keys(this.profileForm.controls).forEach((field) => {
      const control = this.profileForm.get(field);
      if (control) {
        control.markAsTouched();
        control.updateValueAndValidity();
      }
    });
  }

  onSubmit(): void {
    if (this.profileForm.valid) {
      this.changeProfileDetails(this.profileForm.value);
    }
  }

  hasProfileFormChanges(): boolean {
    if (!this.currentUser) {
      return false;
    }
    return (
      this.profileForm.get('display_name')?.value !== this.currentUser.display_name ||
      (this.profileForm.get('img_path')?.value || '').trim() !==
        (this.currentUser.img_path || '').trim()
    );
  }

  changeProfileDetails(profile: any) {
    this.ngxSpinnerService.show();
    this.authService.changeUserProfile(profile).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
        } else {
          this.toastr.success('Profile updated successfully', 'Success');
          this.currentUser = { ...this.currentUser, ...profile };
          this.imagePreview = profile.img_path?.trim() || null;
          this.authService.updatedUserProfile$.next(profile);
          this.updateLocalStorageUser(profile);
        }
        this.ngxSpinnerService.hide();
      },
      error: () => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
      },
    });
  }

  updateLocalStorageUser(profile: any) {
    if (localStorage.getItem('user')) {
      let user: any = localStorage.getItem('user');
      if (user) {
        let jsonUser = JSON.parse(user);
        jsonUser.img_path = profile.img_path;
        localStorage.removeItem('user');
        if (jsonUser !== null) {
          localStorage.setItem('user', JSON.stringify(jsonUser));
        }
      }
    }
  }

  resetForm() {
    if (
      this.profileForm.dirty ||
      this.imagePreview != (this.currentUser.img_path?.trim() || null)
    ) {
      window.location.reload();
    }
  }

  get f() {
    return this.profileForm.controls;
  }

  onFileSelected(event: Event): void {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (file) {
      // The 1MB upload limit is checked after crop + compression
      const sourceError = sourceImageError(file);
      if (sourceError) {
        this.imageSizeError = sourceError;
        return;
      }
      this.imageSizeError = null;
      this.selectedFile = file;

      // Open crop modal
      const reader = new FileReader();
      reader.onload = () => {
        const imageSrc = reader.result as string;
        this.openCropModal(imageSrc);
      };
      reader.readAsDataURL(file);
    }
  }

  openCropModal(imageSrc: string): void {
    const dialogRef = this.dialog.open(ImageCropingComponent, {
      width: '700px',
      maxWidth: '90vw',
      disableClose: true,
      data: {
        imageSrc: imageSrc,
        targetWidth: 300,
        targetHeight: 300,
        maxLongEdge: 800,
        defaultAspectRatio: 'original',
        title: 'Crop Profile Image',
        isImageFrom: 'user-profile',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      const uploadError = result?.blob ? uploadSizeError(result.blob) : null;
      if (result && result.croppedImage && !uploadError) {
        this.imagePreview = result.croppedImage;
        const file = croppedImageFile(result.blob, 'cropped-profile');
        this.selectedFile = file;
        this.setProfileImageFormData(file, 'profile');
      } else {
        if (uploadError) {
          this.imageSizeError = uploadError;
        }
        // User cancelled or the result is too large, reset file input
        const fileInput = document.getElementById('fileInput') as HTMLInputElement;
        if (fileInput) {
          fileInput.value = '';
        }
        this.selectedFile = null;
      }
    });
  }

  removeProfile() {
    this.imagePreview = null;
    this.imageSizeError = null;
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  // User-initiated removal: clear the preview and send CLEARED_IMG_PATH on save.
  clearProfilePhoto() {
    this.removeProfile();
    this.profileForm.get('img_path')?.setValue(CLEARED_IMG_PATH);
  }

  setProfileImageFormData(file: File, fileName: string) {
    let formData = new FormData();
    formData.append('file_id', fileName);
    formData.append('file_type', 'employee');
    formData.append('is_public', 'true');
    formData.append('file', file);
    this.saveProfileImage(formData);
  }

  saveProfileImage(formData: any) {
    this.ngxSpinnerService.show();
    this.restaurantService.addImage(formData).subscribe({
      next: (response: FNFResponse<any>) => {
        if (!response.data && response.error) {
          this.toastr.error(response.error.error?.message || response.error.message, 'Error');
          this.ngxSpinnerService.hide();
          this.removeProfile();
          return;
        } else if (!response.data) {
          this.toastr.error('No data found!', 'Error');
          this.ngxSpinnerService.hide();
          this.removeProfile();
          return;
        }
        this.profileForm.get('img_path')?.setValue(response.data.url);
        this.ngxSpinnerService.hide();
      },
      error: (error) => {
        this.toastr.error('Unable to complete your action. Please try again later.', 'Error');
        this.ngxSpinnerService.hide();
        this.removeProfile();
      },
    });
  }

  onImageError(event: Event): void {
    (event.target as HTMLImageElement).src = this.login_user_img_url;
  }
}
